import {DI_TOKENS} from '@/di/di.tokens';

import {injected} from 'brandi';

import {HttpError, HttpErrorBackend, HttpErrorProject} from '@specials/project-user';
import {CaptureEventParams, CaptureExceptionParams, SentryBrowserAbstract} from '@specials/sentry-browser';

import {ErrorBreadcrumbs} from '@/services/ErrorService/ErrorService';
import {FrontendError} from '@/services/ErrorService/FrontendError';
import {Config} from '@/types';
import {UIErrorComponent} from '@/ui/common/AppError/constants';

export class LoggerService {
  private userId: string | undefined;
  private cookies: string | undefined;

  private readonly errorNames = {
    ErrorBoundaryError: 'ErrorBoundaryError',
    HttpErrorBackend: 'HttpErrorBackend',
    HttpErrorProject: 'HttpErrorProject',
    UnmatchedError: 'UnmatchedError',
    HttpErrorUnknown: 'HttpErrorUnknown',
    UIErrorFullscreenComponent: 'UIErrorFullscreenComponent',
    UIErrorTextComponent: 'UIErrorTextComponent',
    UIErrorActionComponent: 'UIErrorActionComponent',
    CopyToClipboardError: 'CopyToClipboardError',
    AssetsLoadError: 'AssetsLoadError',
    NativeShareError: 'NativeShareError',
    RetrieveStatisticError: 'RetrieveStatisticError',
    FontLoaderError: 'FontLoaderError',
    CacheServiceDataParseError: 'CacheServiceDataParseError',
    DebugInfo: 'DebugInfo',
  };

  constructor(
    private readonly config: Config,
    private readonly sentryBrowser: SentryBrowserAbstract,
  ) {}

  public setUserId(userId: string | undefined): void {
    this.userId = userId;
  }

  public setCookies(cookies: string | undefined): void {
    this.cookies = cookies;
  }

  public logErrorBoundaryError(error: FrontendError): void {
    this.error({
      message: this.errorNames.ErrorBoundaryError,
      trackingId: error.requestId,
      payload: {
        errorCode: error.errorCode,
        message: error.message,
        timestamp: error.timestamp,
      },
      error: error.error,
    });
  }

  public logHttpError(error: HttpError): void {
    if (HttpErrorProject.isHttpErrorProject(error)) {
      this.error({
        message: this.errorNames.HttpErrorProject,
        trackingId: error.requestId,
        payload: {
          errorCode: error.errorCode,
          message: error.message,
          timestamp: error.timestamp,
        },
        error,
      });

      return;
    }

    if (HttpErrorBackend.isHttpErrorBackend(error)) {
      this.error({
        message: this.errorNames.HttpErrorBackend,
        payload: {
          httpCode: error.response.httpCode,
          message: error.message,
          name: error.name,
        },
        error,
      });

      return;
    }

    this.error({
      message: this.errorNames.HttpErrorUnknown,
      payload: {
        message: error.message,
        name: error.name,
      },
      error,
    });
  }

  public logUnMatchedError(breadcrumbs: ErrorBreadcrumbs): void {
    this.error({
      message: this.errorNames.UnmatchedError,
      trackingId: breadcrumbs.requestId,
      payload: {
        ...breadcrumbs,
      },
    });
  }

  public logNativeShareError(error: FrontendError): void {
    this.error({
      message: this.errorNames.NativeShareError,
      trackingId: error?.requestId,
      payload: {
        errorCode: error?.errorCode,
        message: error?.message,
        timestamp: error?.timestamp,
      },
      error: error.error,
    });
  }

  public logCopyToClipboardError(error: FrontendError): void {
    this.error({
      message: this.errorNames.CopyToClipboardError,
      trackingId: error?.requestId,
      payload: {
        errorCode: error?.errorCode,
        message: error?.message,
        timestamp: error?.timestamp,
      },
      error: error.error,
    });
  }

  public logAssetsLoadError(error: FrontendError): void {
    this.error({
      message: this.errorNames.AssetsLoadError,
      trackingId: error?.requestId,
      payload: {
        errorCode: error?.errorCode,
        message: error?.message,
        timestamp: error?.timestamp,
      },
      error: error.error,
    });
  }

  public logParseStatisticError(error: FrontendError): void {
    this.error({
      message: this.errorNames.RetrieveStatisticError,
      trackingId: error?.requestId,
      payload: {
        errorCode: error?.errorCode,
        message: error?.message,
        timestamp: error?.timestamp,
      },
      error: error.error,
    });
  }

  public logFontLoaderError(error: FrontendError): void {
    this.error({
      message: this.errorNames.FontLoaderError,
      trackingId: error?.requestId,
      payload: {
        errorCode: error?.errorCode,
        message: error?.message,
        timestamp: error?.timestamp,
      },
      error: error.error,
    });
  }

  public logCacheServiceDataParseError(error: FrontendError): void {
    this.error({
      message: this.errorNames.CacheServiceDataParseError,
      trackingId: error?.requestId,
      payload: {
        errorCode: error?.errorCode,
        message: error?.message,
        timestamp: error?.timestamp,
      },
      error: error.error,
    });
  }

  // логируем отображение UI компонента ошибки
  public logDisplayErrorUIComponent(
    error: FrontendError | HttpError | unknown,
    breadcrumbs: ErrorBreadcrumbs,
    component: UIErrorComponent,
  ): void {
    let message = '';

    switch (component) {
      case UIErrorComponent.Text: {
        message = this.errorNames.UIErrorTextComponent;
        break;
      }

      case UIErrorComponent.Fullscreen: {
        message = this.errorNames.UIErrorFullscreenComponent;
        break;
      }

      case UIErrorComponent.Action: {
        message = this.errorNames.UIErrorActionComponent;
        break;
      }

      default: {
        message = this.errorNames.UIErrorTextComponent;
        break;
      }
    }

    if (HttpErrorProject.isHttpErrorProject(error)) {
      this.warn({
        message,
        trackingId: error.requestId,
        payload: {
          errorCode: error.errorCode,
          message: error.errorMessage,
          timestamp: error.timestamp,
          errorName: this.errorNames.HttpErrorProject,
          ...breadcrumbs,
        },
      });

      return;
    }

    if (HttpErrorBackend.isHttpErrorBackend(error)) {
      this.warn({
        message,
        payload: {
          httpCode: error.response.httpCode,
          message: error.message,
          name: error.name,
          errorName: this.errorNames.HttpErrorBackend,
          ...breadcrumbs,
        },
      });

      return;
    }

    if (FrontendError.isFrontendError(error)) {
      this.warn({
        message,
        trackingId: error.requestId,
        payload: {
          errorCode: error.errorCode,
          message: error.message,
          timestamp: error.timestamp,
          errorName: error.name,
          ...breadcrumbs,
        },
      });

      return;
    }

    this.warn({
      message,
      payload: {
        errorName: this.errorNames.HttpErrorUnknown,
        ...breadcrumbs,
      },
    });
  }

  public logDebugInfo(data: unknown): void {
    this.warn({
      message: this.errorNames.DebugInfo,
      payload: {
        data,
      },
    });
  }

  public log(name: string, message: unknown): void {
    if (!this.config.isProd) {
      console.log(name, message);
    }
  }

  public logError(...args: unknown[]): void {
    if (!this.config.isProd) {
      console.error(...args);
    }
  }

  private warning(message: string): void {
    if (!this.config.isProd) {
      console.warn(message);
    }
  }

  private error(params: CaptureExceptionParams): void {
    if (!this.config.isProd) {
      console.error(params.message, params.error);
    }

    const sentParams: CaptureExceptionParams = {
      message: params.message,
      error: params.error as Error,
      trackingId: params?.trackingId,
      payload: {
        ...params.payload,
        userId: this.userId,
        cookies: this.cookies,
      },
    };

    this.sentryBrowser.captureException(sentParams);
  }

  private warn(params: CaptureEventParams): void {
    if (!this.config.isProd) {
      console.error(params.message, params.payload);
    }

    const sentParams: CaptureEventParams = {
      message: params.message,
      trackingId: params?.trackingId,
      payload: {
        ...params.payload,
        userId: this.userId,
        cookies: this.cookies,
      },
    };

    // отправляем события через captureEvent,
    // т.к. в captureException встроен троттлинг на отправку сообщений
    this.sentryBrowser.captureEvent(sentParams);
  }
}

injected(LoggerService, DI_TOKENS.config, DI_TOKENS.sentryBrowser);
